import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import ProfileImage from '../Header/ProfileImage';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import SkeletonClientItem from '../Clients/SkeletonClientItem';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal } from '../../store/actions/modalActions';
import { PiTrashLight, PiPencilLight, PiUploadLight, PiSealCheckLight, PiLockKeyLight } from 'react-icons/pi';
import PeopleForm from '../Management/Employees/PeopleForm';
import ImportUsersWizard from '../Import/ImportUsersWizard';
import { revokeUserFromAddon } from '../../store/actions/PlanActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';

interface SeatInfo {
  seat_count: number;
  seats_used: number;
  seats_available: number;  // Kept for backwards compat — may be null for base plans
  seats_assigned?: number;  // New: derived count of active assignments
  plan_name: string | null;
  plan_type: string | null;
  can_invite: boolean;
  org_license_id?: number | null;
}

interface User {
  id: number;
  uuid?: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  is_org_admin: boolean;
  is_active: boolean;
  created_at: string;
  has_base_seat?: boolean;
}

interface AdminUsersProps {
  openInviteModal?: boolean;
  onCloseInvite?: () => void;
  openImportModal?: boolean;
  onCloseImport?: () => void;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ openInviteModal = false, onCloseInvite, openImportModal = false, onCloseImport }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);
  const [baseOrgLicenseId, setBaseOrgLicenseId] = useState<number | null>(null);
  const [seatActionLoading, setSeatActionLoading] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const adminCount = users.filter(u => u.is_org_admin).length;

  // Users eligible for seat assignment (not admin, not already seated)
  const unseatedUsers = users.filter(u => !u.is_org_admin && !u.has_base_seat);
  const prevOpenInviteModalRef = React.useRef(false);
  const prevOpenImportModalRef = React.useRef(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (openImportModal && !prevOpenImportModalRef.current) {
      setIsImporting(true);
    }
    prevOpenImportModalRef.current = openImportModal;
  }, [openImportModal]);

  useEffect(() => {
    if (openInviteModal && !prevOpenInviteModalRef.current) {
      setIsInviting(true);
    }
    prevOpenInviteModalRef.current = openInviteModal;
  }, [openInviteModal]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apis.get('/admin/users');
      if (response.data.success) {
        setUsers(response.data.data || []);
        setFilteredUsers(response.data.data || []);
        if (response.data.seat_info) {
          setSeatInfo(response.data.seat_info);
        }
        setBaseOrgLicenseId(
          response.data.base_org_license_id ?? response.data.seat_info?.org_license_id ?? null
        );
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.error || t('Failed to fetch users'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to fetch users'),
        modalType: 'error',
        duration: 4000
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apis.get('/admin/roles');
      if (response.data.success) {
        const lawFirmRoles = response.data.data.filter((role: any) => {
          const roleName = role.name.toLowerCase();
          const excludedRoles = ['client', 'external', 'guest'];
          return !excludedRoles.some(excluded => roleName.includes(excluded));
        });
        const roleOptions = lawFirmRoles.map((role: any) => ({
          value: role.name,
          label: formatRoleNameForDisplay(role.name),
        }));
        setRoles(roleOptions);
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.error || t('Failed to fetch roles'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to fetch roles'),
        modalType: 'error',
        duration: 4000
      }));
    }
  };

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleSearchChange = (query: string) => {
    setSearchTerm(query);
  };

  const userFilters = [
    {
      id: 'Status',
      name: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ];

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    // Filter already applied through searchTerm
    // This is here for consistency with other pages
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
  };

  const handleInviteCancel = () => {
    setIsInviting(false);
    onCloseInvite?.();
  };

  const handleInviteConfirm = () => {
    setIsInviting(false);
    fetchUsers();
    onCloseInvite?.();
  };

  const handleAddUser = () => {
    // No seat cap — admins can always invite. Billing adjusts automatically.
    setIsInviting(true);
  };

  const openAssignPanel = (targetUserIds: number[]) => {
    const names = targetUserIds.map(id => {
      const u = users.find(user => user.id === id);
      return u ? (`${u.first_name} ${u.last_name}`.trim() || u.email) : String(id);
    });
    dispatch(showSidePanel({
      type: 'assignLicense',
      showSidePanel: true,
      width: '50%',
      icon: <PiSealCheckLight className="w-5 h-5 text-gray-600 dark:text-gray-300" />,
      message: targetUserIds.length === 1 ? t('Assign License') : t('Assign Seats'),
      subMsg: targetUserIds.length === 1
        ? t('Select a plan and optional add-ons for this user.')
        : t('Assign a base plan to {{count}} selected users.', { count: targetUserIds.length }),
      userIds: targetUserIds,
      userNames: names,
      onComplete: () => {
        setSelectedUserIds(new Set());
        fetchUsers();
        dispatch(hideSidePanel());
      },
    }));
  };

  const handleSelectUser = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      const numId = Number(id);
      if (next.has(numId)) {
        next.delete(numId);
      } else {
        next.add(numId);
      }
      return next;
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.size === unseatedUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(unseatedUsers.map(u => u.id)));
    }
  };

  const handleRowClick = (row: any) => {
    history.push(`/admin/users/${row.rawUser.id}`);
  };

  const handleAssignSeat = (user: User) => {
    openAssignPanel([user.id]);
  };

  const handleRevokeSeat = async (user: User) => {
    if (!baseOrgLicenseId) return;
    if (user.is_org_admin) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Admins always have an implicit seat and cannot be revoked.'),
        modalType: 'info',
        duration: 4000,
      }));
      return;
    }
    if (!window.confirm(
      t('Revoke {{name}}\'s seat? They will lose access to licensed features until reassigned.', {
        name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      })
    )) {
      return;
    }
    setSeatActionLoading(user.id);
    try {
      const ok = await dispatch(
        revokeUserFromAddon({ org_license_id: baseOrgLicenseId, user_id: user.id })
      );
      if (ok) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Seat revoked from {{name}}', {
            name: `${user.first_name} ${user.last_name}`.trim() || user.email,
          }),
          modalType: 'success',
          duration: 3000,
        }));
        await fetchUsers();
      }
    } finally {
      setSeatActionLoading(null);
    }
  };

  const handleEditUser = (user: User) => {
    dispatch(showModal({
      showModal: true,
      type: 'adminEditUser',
      user,
      item: { adminCount },
      message: t('Edit User'),
      onConfirm: async (updatedUser: any) => {
        try {
          // If role changed, assign new role
          if (updatedUser.role && updatedUser.role !== user.role) {
            const roleResponse = await apis.post(`/admin/users/${user.id}/roles`, {
              role_name: updatedUser.role,
            });

            if (!roleResponse.data.success) {
              dispatch(showFeedbackModal({
                showModal: true,
                message: roleResponse.data.error || t('Failed to assign role'),
                modalType: 'error',
                duration: 4000
              }));
              return;
            }
          }

          setUsers(
            users.map((u) =>
              u.id === user.id
                ? { ...u, role: updatedUser.role, is_org_admin: updatedUser.is_org_admin }
                : u
            )
          );
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('User updated successfully'),
            modalType: 'success',
            duration: 3000
          }));
        } catch (error: any) {
          console.error('Error updating user:', error);
          dispatch(showFeedbackModal({
            showModal: true,
            message: error?.response?.data?.error || t('Failed to update user'),
            modalType: 'error',
            duration: 4000
          }));
        }
      }
    }));
  };

  const handleRemoveUser = async (userId: number) => {
    if (!window.confirm(t('Are you sure you want to remove this user from the organization?'))) {
      return;
    }

    try {
      const response = await apis.delete(`/users/${userId}`);

      if (response.data.success) {
        setUsers(users.filter((u) => u.id !== userId));
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('User removed successfully'),
          modalType: 'success',
          duration: 3000
        }));
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.error || t('Failed to remove user'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      console.error('Error removing user:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: error?.response?.data?.error || t('Failed to remove user'),
        modalType: 'error',
        duration: 4000
      }));
    }
  };

  const columns = ['Name', 'Admin', 'Email', 'Role', 'Seat', 'Status', 'Actions'];

  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim();
  };

  const tableData = filteredUsers.map((user) => ({
    id: String(user.id),
    'Name': (
      <div className="flex items-center gap-3">
        <ProfileImage
          profilePicture={null}
          email={user.email}
          initials={getInitials(user.first_name, user.last_name)}
          name={`${user.first_name} ${user.last_name}`}
          size="h-8 w-8"
        />
        <p className="font-medium text-gray-900 dark:text-white">{`${user.first_name} ${user.last_name}`}</p>
      </div>
    ),
    'Admin': (
      <Badge color={user.is_org_admin ? 'green' : 'red'}>
        {user.is_org_admin ? 'True' : 'False'}
      </Badge>
    ),
    'Email': <span className="text-sm text-gray-700 dark:text-gray-300">{user.email}</span>,
    'Role': (
      <span className="text-sm text-gray-700 dark:text-gray-300">
        {user.role ? formatRoleNameForDisplay(user.role) : '-'}
      </span>
    ),
    'Seat': (
      user.is_org_admin ? (
        <Badge color="blue">{t('Admin')}</Badge>
      ) : user.has_base_seat ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <PiSealCheckLight className="h-4 w-4" />
          {t('Seated')}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
          <PiLockKeyLight className="h-4 w-4" />
          {t('No seat')}
        </span>
      )
    ),
    'Status': (
      <Badge color={user.is_active ? 'green' : 'gray'}>
        {user.is_active ? t('Active') : t('Inactive')}
      </Badge>
    ),
    'Actions': null,
    rawUser: user,
  }));

  const userActions = [
    {
      label: t('Edit'),
      onClick: (row: any) => handleEditUser(row.rawUser),
      icon: <PiPencilLight className="h-4 w-4" />
    },
    {
      label: t('Assign license'),
      onClick: (row: any) => handleAssignSeat(row.rawUser),
      icon: <PiSealCheckLight className="h-4 w-4 text-emerald-600" />,
      hidden: (row: any) => row.rawUser.is_org_admin || row.rawUser.has_base_seat === true,
    },
    {
      label: t('Manage license'),
      onClick: (row: any) => openAssignPanel([row.rawUser.id]),
      icon: <PiSealCheckLight className="h-4 w-4" />,
      hidden: (row: any) => row.rawUser.is_org_admin || row.rawUser.has_base_seat !== true,
    },
    {
      label: t('Revoke seat'),
      onClick: (row: any) => handleRevokeSeat(row.rawUser),
      icon: <PiLockKeyLight className="h-4 w-4 text-amber-600" />,
      hidden: (row: any) => row.rawUser.is_org_admin || row.rawUser.has_base_seat !== true,
      disabled: (row: any) => seatActionLoading === row.rawUser.id,
    },
    {
      label: t('Remove'),
      onClick: (row: any) => handleRemoveUser(row.rawUser.id),
      icon: <PiTrashLight className="h-4 w-4 text-red-600" />,
      isDestructive: true,
      disabled: (row: any) => row.rawUser.id === userInfo?.UserUID
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {isInviting ? (
        <div className="w-full h-screen">
          <PeopleForm
            onSuccess={handleInviteConfirm}
            onCancel={handleInviteCancel}
            inviteType="admin"
          />
        </div>
      ) : (
        <>
          <div className="py-2">
            <div className="flex justify-between items-center gap-3">
              <div className="flex-1">
                <FilterBarWithTabs
                  filters={userFilters}
                  selectedFilters={{}}
                  onFilterChange={handleFilterChange}
                  onSearchChange={handleSearchChange}
                  onClearAllFilters={handleClearAllFilters}
                  noParentPadding
                  selectionCount={selectedUserIds.size}
                  actions={selectedUserIds.size > 0 ? [
                    {
                      label: t('Assign Seats'),
                      onClick: () => openAssignPanel(Array.from(selectedUserIds)),
                      icon: <PiSealCheckLight className="h-4 w-4" />,
                    },
                  ] : []}
                />
              </div>
            </div>
          </div>


          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="">
                <SkeletonClientItem viewMode="table" count={3} />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-600 dark:text-gray-400">{t('No users found')}</p>
              </div>
            ) : (
              <div className="">
                <Table
                  columns={columns}
                  data={tableData}
                  actions={userActions}
                  onRowClick={handleRowClick}
                  enableSelection={unseatedUsers.length > 0}
                  selectedIds={Array.from(selectedUserIds).map(String)}
                  onSelect={handleSelectUser}
                  onSelectAll={handleSelectAllUsers}
                  total_items={users.length}
                  total_showing_items={filteredUsers.length}
                  setIsShowing={true}
                  showingLabel={t('users')}
                />
              </div>
            )}
          </div>
        </>
      )}
      <ImportUsersWizard
        isOpen={isImporting}
        onClose={() => { setIsImporting(false); onCloseImport?.(); }}
        onComplete={() => { fetchUsers(); setIsImporting(false); onCloseImport?.(); }}
      />
    </div>
  );
};

export default AdminUsers;
